import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CssBaseline, ThemeProvider, createTheme, Paper, Typography, CircularProgress } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Authentication imports
import { AuthProvider, useAuth } from './context/AuthContext';
import {
  LoginPage,
  RegistrationPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  EmailVerificationPage,
  UserProfilePage,
  ProtectedRoute
} from './components/Auth';
import AccessRequestDialog from './components/Auth/AccessRequestDialog';
import PendingApprovalBanner from './components/Auth/PendingApprovalBanner';

// User Management imports
import { UserListPage, OrganizationListPage, OrganizationDetailsPage } from './components/UserManagement';

// Contractor Management imports
import { ContractorListPage } from './components/ContractorManagement';
import { ContractorWorkOrderDashboard, WorkOrderListPage, ContractorWorkOrderDetails, WorkOrderAnalyticsDashboard } from './components/WorkOrders';

// Existing component imports
import Navbar from './components/Navbar';
import Status from './components/Status';
import AssetsPage from './components/Assets/AssetsPage';
import MapViewPage from './components/MapView/MapViewPage';
import PDFReportPage from './components/MapView/modals/PDFReportPage';
import DashboardPage from './components/Dashboard/DashboardPage';

import DefectsENVISPage from './components/Defects(ENVIS)/DefectsPage';
import AerialDefectsPage from './components/Defects(ENVIS)/AerialDefectsPage';
import { AnalyticsPage } from './components/Analytics';
import { ReportsPage, RoadReportsPage, BridgeReportsPage, SlopeReportsPage, HighMastReportsPage } from './components/Reports';
import { CesiumViewerProvider } from './context/CesiumViewerContext';
import DevicePage from './components/Devices/DevicePage';
import SettingsPage from './components/Settings/SettingsPage';
import GeoJsonManagementPage from './components/Settings/GeoJsonManagementPage';
import AiJobStatusPage from './components/AiJobStatus/AiJobStatusPage';
import { LiveViewPage } from './components/LiveCamera';
import EmailFlowPage from './components/EmailFlow/EmailFlowPage';
import InspectionFlowPage from './components/InspectionFlow/InspectionFlowPage';

// Dashboard imports
import { ManagerDashboard } from './components/Dashboard';

// Create a theme instance
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#f50057',
    },
    background: {
      default: '#f5f7fa',
    },
  },
  typography: {
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
});

// Placeholder components for the routes with MUI styling

const Sensors = () => (
  <Box sx={{ p: 2 }}>
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h5" gutterBottom>Sensors</Typography>
      <Typography variant="body1">Monitor IoT sensors and data</Typography>
    </Paper>
  </Box>
);

// Footer component that checks the current route
const Footer = () => {
  const location = useLocation();
  const authPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email'];
  
  // Hide footer on map page and all auth pages
  if (location.pathname === '/map-view' || authPaths.includes(location.pathname)) {
    return null;
  }
  
  return (
    <Box 
      component="footer" 
      sx={{ 
        py: 2, 
        px: 2, 
        mt: 'auto', // Pushes footer to bottom if MainContainer has flex-grow
        backgroundColor: 'white',
        borderTop: '1px solid',
        borderColor: 'divider',
        textAlign: 'center',
        flexShrink: 0 // Prevent footer from shrinking
      }}
    >
      <Typography variant="body2" color="text.secondary">
        IRIS Road Asset Management Platform - © 2025
      </Typography>
    </Box>
  );
};

// Preserve legacy /defects?defect_id=... links by redirecting to ground-view with query intact
const DefectsLegacyRedirect = () => {
  const location = useLocation();
  return (
    <Navigate to={{ pathname: '/defects/ground-view', search: location.search }} replace />
  );
};

// Main container component that adapts to the current route
const MainContainer = () => {
  const location = useLocation();
  const isMapPage = location.pathname === '/map-view';
  // Updated to include all auth pages for specific styling
  const isAuthPage = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email'].includes(location.pathname);
  // PDF report preview opens in its own tab with a sticky download bar that should sit flush
  // against the page footer, so we drop MainContainer's inset padding for these routes.
  const isReportPreviewPage = location.pathname.startsWith('/report-preview');
  
  // Determine if Navbar and Footer are *actually* visible for height calculation
  // Navbar is hidden by AppLayout if isLoginPage (which is a subset of isAuthPage)
  const navbarIsActuallyVisible = !authPaths.includes(location.pathname); // Simplified: navbar hidden on all auth pages by AppLayout change
  // Footer hides itself on map page or any auth page
  const footerIsActuallyVisible = !isMapPage && !authPaths.includes(location.pathname);

  let heightExpression = "100vh";
  if (navbarIsActuallyVisible) {
    heightExpression += " - 64px"; // Assuming 64px Navbar height
  }
  if (footerIsActuallyVisible) {
    heightExpression += " - 51px"; // Assuming 51px Footer height (approx. py:2 + border + text)
  }
  const finalHeight = `calc(${heightExpression})`;

  return (
    <Box 
      component="main" 
      sx={{ 
        bgcolor: isAuthPage ? 'transparent' : 'background.default', // Auth pages have their own BG
        overflow: (isMapPage || isAuthPage) ? 'hidden' : 'auto', // Auth pages often handle their own scroll or are fixed
        display: 'flex',
        flexDirection: 'column',
        position: 'relative', // Needed for some children, like maps
        height: isAuthPage ? '100vh' : finalHeight, // Auth pages take full viewport height directly
        padding: (isMapPage || isAuthPage || isReportPreviewPage) ? 0 : 2, // No padding for auth/map/report-preview pages; others get theme padding (e.g., p:2)
        flexGrow: isAuthPage ? 0 : 1, // Main content should grow for non-auth pages
      }}
    >
      <Routes>
        {/* Public routes - these will now be directly under MainContainer which is 100vh for them */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegistrationPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<EmailVerificationPage />} />

        {/* PDF Report Preview - Opens in new tab, protected route */}
        <Route path="/report-preview/:inspectionId" element={
          <ProtectedRoute>
            <PDFReportPage />
          </ProtectedRoute>
        } />

        {/* Protected routes - require authentication */}
        <Route path="/" element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } />
        
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } />
        
        <Route path="/dashboard/:type" element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } />
        
        <Route path="/live-view" element={
          <ProtectedRoute>
            <LiveViewPage />
          </ProtectedRoute>
        } />
        
        <Route path="/assets" element={
          <ProtectedRoute>
            <AssetsPage />
          </ProtectedRoute>
        } />
        
        {/* Defects routes with dropdown options */}
        <Route path="/defects" element={<DefectsLegacyRedirect />} />
        
        <Route path="/defects/ground-view" element={
          <ProtectedRoute>
            <DefectsENVISPage />
          </ProtectedRoute>
        } />
        
        <Route path="/defects/aerial-view" element={
          <ProtectedRoute>
            <AerialDefectsPage />
          </ProtectedRoute>
        } />
        
        <Route path="/analytics" element={
          <ProtectedRoute>
            <AnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="/analytics/:type" element={
          <ProtectedRoute>
            <AnalyticsPage />
          </ProtectedRoute>
        } />
        
        <Route path="/work-orders" element={
          <ProtectedRoute requiredRoles={['manager']}>
            <WorkOrderListPage />
          </ProtectedRoute>
        } />
        
        <Route path="/work-orders/analytics" element={
          <ProtectedRoute requiredRoles={['manager']}>
            <WorkOrderAnalyticsDashboard />
          </ProtectedRoute>
        } />
        
        <Route path="/manager-dashboard" element={
          <ProtectedRoute requiredRoles={['manager']}>
            <ManagerDashboard />
          </ProtectedRoute>
        } />
        
        <Route path="/sensors" element={
          <ProtectedRoute>
            <Sensors />
          </ProtectedRoute>
        } />
        
        <Route path="/reports" element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        } />
        
        <Route path="/reports/road" element={
          <ProtectedRoute>
            <RoadReportsPage />
          </ProtectedRoute>
        } />
        
        <Route path="/reports/bridge" element={
          <ProtectedRoute>
            <BridgeReportsPage />
          </ProtectedRoute>
        } />
        
        <Route path="/reports/slope" element={
          <ProtectedRoute>
            <SlopeReportsPage />
          </ProtectedRoute>
        } />

        <Route path="/reports/high-mast" element={
          <ProtectedRoute>
            <HighMastReportsPage />
          </ProtectedRoute>
        } />

        <Route path="/status" element={
          <ProtectedRoute>
            <Status />
          </ProtectedRoute>
        } />
        
        <Route path="/settings" element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } />
        
        <Route path="/map-layers" element={
          <ProtectedRoute requiredRoles={['manager', 'admin']}>
            <GeoJsonManagementPage />
          </ProtectedRoute>
        } />
        
        <Route path="/devices" element={
          <ProtectedRoute>
            <DevicePage />
          </ProtectedRoute>
        } />

        <Route path="/ai-job-status" element={
          <ProtectedRoute>
            <AiJobStatusPage />
          </ProtectedRoute>
        } />
        
        <Route path="/map-view" element={
          <ProtectedRoute>
            <CesiumViewerProvider>
              <MapViewPage />
            </CesiumViewerProvider>
          </ProtectedRoute>
        } />
        
        <Route path="/profile" element={
          <ProtectedRoute>
            <UserProfilePage />
          </ProtectedRoute>
        } />
        
        <Route path="/users" element={
          <ProtectedRoute requiredRoles={['admin', 'manager']}>
            <UserListPage />
          </ProtectedRoute>
        } />
        
        <Route path="/email-flow" element={
          <ProtectedRoute requiredRoles={['admin', 'manager']}>
            <EmailFlowPage />
          </ProtectedRoute>
        } />

        <Route path="/inspection-flow" element={
          <ProtectedRoute requiredRoles={['admin', 'manager']}>
            <InspectionFlowPage />
          </ProtectedRoute>
        } />

        <Route path="/contractors" element={
          <ProtectedRoute requiredRoles={['manager']}>
            <ContractorListPage />
          </ProtectedRoute>
        } />
        
        <Route path="/my-work-orders" element={
          <ProtectedRoute requiredRoles={['contractor']}>
            <ContractorWorkOrderDashboard />
          </ProtectedRoute>
        } />
        
        <Route path="/my-work-orders/:id" element={
          <ProtectedRoute requiredRoles={['contractor', 'manager', 'admin']}>
            <ContractorWorkOrderDetails />
          </ProtectedRoute>
        } />
        
        <Route path="/organizations" element={
          <ProtectedRoute requiredRoles={['admin']}>
            <OrganizationListPage />
          </ProtectedRoute>
        } />
        
        <Route path="/organizations/:id" element={
          <ProtectedRoute requiredRoles={['admin']}>
            <OrganizationDetailsPage />
          </ProtectedRoute>
        } />
      </Routes>
    </Box>
  );
};

const authPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email'];

// Layout component that conditionally renders Navbar and Footer
const AppLayout = () => {
  const location = useLocation();
  const { isLoading, needsSetup, isPendingApproval } = useAuth();
  const currentIsAuthPage = authPaths.includes(location.pathname);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', overflow: 'hidden' }}>
      {/* Hide Navbar on all auth pages */}
      {!currentIsAuthPage && <Navbar />}
      {/* Pending approval banner for SSO users awaiting manager review */}
      {isPendingApproval && !currentIsAuthPage && <PendingApprovalBanner />}
      <MainContainer />
      {/* Footer is now also conditionally rendered based on auth page status more explicitly at AppLayout level if needed, but its internal logic is good */}
      <Footer />
      {/* Blocking dialog for first-time SSO users who need to set up their access */}
      {needsSetup && <AccessRequestDialog />}
    </Box>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <AuthProvider>
          <Router>
            <AppLayout />
          </Router>
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
